import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  centerElementHorizontally,
  deleteElement,
  setLayerModalOpen,
  toggleAspectLock,
} from "@/store/slideEditorSlice";
import { IconButton } from "@ai-teach/ui-web";
import { useEffect, useState } from "react";
import { FaLock, FaUnlock } from "react-icons/fa";
import { MdAlignHorizontalCenter, MdLayers } from "react-icons/md";
import { RiDeleteBin6Fill } from "react-icons/ri";

export function ElementActionButtons() {
  const dispatch = useAppDispatch();
  const { selectedId, isLayerModalOpen } = useAppSelector((state) => state.slideEditor.present);
  const isAspectLock = useAppSelector((state) => state.slideEditor.present.isAspectLocked);
  const [position, setPosition] = useState<{ top: number; left: number } | undefined>(undefined);

  const handleAspectRatio = () => {
    dispatch(toggleAspectLock());
  };

  const handleCenterHorizontally = () => {
    if (selectedId) {
      dispatch(centerElementHorizontally(selectedId));
    }
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (selectedId) {
      dispatch(deleteElement(selectedId));
    }
  };

  const handleLayerToggle = () => {
    dispatch(setLayerModalOpen(!isLayerModalOpen));
  };

  // Update position continuously to follow the selected element
  useEffect(() => {
    if (!selectedId) {
      setPosition(undefined);
      return;
    }

    const domElement = document.querySelector(`[data-element-id="${selectedId}"]`);

    const calculatePosition = (element: Element | null): { top: number; left: number } | null => {
      if (!element) return null;

      const rect = element.getBoundingClientRect();
      const toolbarWidth = 200; // Approximate width
      const toolbarHeight = 50; // Approximate height
      const gap = 10;

      let left = rect.left + rect.width / 2;
      let top = rect.bottom + gap;

      // Check bottom boundary - if toolbar would go off screen, position above element
      if (top + toolbarHeight > window.innerHeight) {
        top = rect.top - toolbarHeight - gap;
      }

      // Check top boundary - if still off screen, position at top edge
      if (top < 0) {
        top = gap;
      }

      // Check right boundary - center is offset by half width (translateX(-50%))
      if (left + toolbarWidth / 2 > window.innerWidth) {
        left = window.innerWidth - toolbarWidth / 2 - gap;
      }

      if (left - toolbarWidth / 2 < 0) {
        left = toolbarWidth / 2 + gap;
      }

      return { top, left };
    };

    const updatePosition = () => {
      const newPosition = calculatePosition(domElement);

      if (newPosition) {
        setPosition(newPosition);
      }
    };

    updatePosition();

    window.addEventListener("resize", updatePosition);

    let observer: MutationObserver | null = null;

    if (domElement) {
      observer = new MutationObserver(updatePosition);
      observer.observe(domElement, { attributes: true, attributeFilter: ["style"] });
    }

    return () => {
      window.removeEventListener("resize", updatePosition);
      if (observer) observer.disconnect();
    };
  }, [selectedId]);

  if (!selectedId || !position) return null;

  return (
    <div
      data-screenshot-hide="true"
      className="fixed bg-background backdrop-blur-md rounded-xl shadow-xl border border-white/60 z-[90] -translate-x-1/2"
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`,
      }}
    >
      <div className="flex items-center gap-2 p-2">
        {/* Aspect Ratio Lock/Unlock Button */}
        <IconButton
          variant="ghost"
          size="sm"
          onClick={handleAspectRatio}
          title={isAspectLock ? "Unlock aspect ratio" : "Lock aspect ratio"}
          aria-label={isAspectLock ? "Unlock aspect ratio" : "Lock aspect ratio"}
        >
          {isAspectLock ? <FaUnlock className="w-4 h-4" /> : <FaLock className="w-4 h-4" />}
        </IconButton>

        {/* Divider */}
        <div className="w-px h-6 bg-gradient-to-b from-transparent via-slate-200 to-transparent" />

        {/* Center Horizontally Button */}
        <IconButton
          variant="ghost"
          size="sm"
          onClick={handleCenterHorizontally}
          title="Center horizontally"
          aria-label="Center horizontally"
        >
          <MdAlignHorizontalCenter className="w-4 h-4" />
        </IconButton>

        {/* Divider */}
        <div className="w-px h-6 bg-gradient-to-b from-transparent via-slate-200 to-transparent" />

        {/* Delete Button */}
        <IconButton
          variant="ghost"
          size="sm"
          onClick={handleDelete}
          title="Delete element"
          aria-label="Delete element"
          className="hover:bg-rose-50 hover:text-rose-600"
        >
          <RiDeleteBin6Fill className="w-4 h-4" />
        </IconButton>

        {/* Divider */}
        <div className="w-px h-6 bg-gradient-to-b from-transparent via-slate-200 to-transparent" />

        {/* Layers Button */}
        <IconButton
          variant="ghost"
          size="sm"
          onClick={handleLayerToggle}
          data-layer-button="true"
          title="Layer controls"
          aria-label="Layer controls"
        >
          <MdLayers className="w-4 h-4" />
        </IconButton>
      </div>
    </div>
  );
}
